import { inject, Injectable, signal } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivateChildFn,
	CanActivateFn,
	Router,
	RoutesRecognized,
} from '@angular/router';
import { BehaviorSubject, filter, pairwise } from 'rxjs';
import { AuthService } from './auth.service';
import {
	AlarisLanguageService,
	AlarisLocalStorageService,
	AlarisToasterService,
	TokenStatus,
} from '@campaign-portal/components-library';
import { UserService } from './user.service';
import { STORAGE } from '@helpers/types/consts';
import { routerLinks } from '@helpers/shared/router-links.const';

@Injectable({
	providedIn: 'root',
})
export class AuthGuardService {
	readonly previousUrl = new BehaviorSubject<string>('');
	readonly route = signal<ActivatedRouteSnapshot | null>(null);

	constructor(
		private readonly authService: AuthService,
		private readonly storage: AlarisLocalStorageService,
		private readonly toaster: AlarisToasterService,
		private readonly lSerive: AlarisLanguageService,
		private readonly router: Router,
		private readonly user: UserService
	) {
		this.router.events
			.pipe(
				filter((evt: any) => evt instanceof RoutesRecognized),
				pairwise()
			)
			.subscribe((events: RoutesRecognized[]) => {
				this.previousUrl.next(events[0].urlAfterRedirects);
			});
	}

	public getFirstAvailableUrl(): string {
		const topLevel = this.getFirstAvailableTopLevelRoute();
		return topLevel ?? '/';
	}

	canActivate(route: ActivatedRouteSnapshot): boolean {
		if (!this.authService.logged) {
			this.handleLogout();
			this.router.navigate(['/sign-in'], { replaceUrl: true });
			return false;
		}

		this.route.set(route);

		const permissions = route.data?.['permissions'] ?? [];
		const isAllowed = permissions.length === 0 || this.user.allowed(permissions);

		if (isAllowed) {
			return true;
		}

		const fallbackUrl = this.findFirstAvailableUrl(route);
		if (fallbackUrl) {
			this.router.navigateByUrl(fallbackUrl, { replaceUrl: true });
			return false;
		}

		this.router.navigate(['/'], { replaceUrl: true });
		return false;
	}

	canActivateChild(route: ActivatedRouteSnapshot): boolean {
		return this.canActivate(route);
	}

	private handleLogout(): void {
		if (
			this.authService.checkTokenExpired(this.storage.get(STORAGE.TOKEN)) ===
			TokenStatus.EXPIRED
		) {
			this.toaster.error(
				this.lSerive.translate('errors.unauthorizedExtraError'),
				this.lSerive.translate('signIn.title')
			);
		}
		this.route.set(null);
		this.authService.logOut();
	}

	private getFullPath(route: ActivatedRouteSnapshot): string {
		const segments: string[] = [];
		let current: ActivatedRouteSnapshot | null = route;
		while (current) {
			const path = current.routeConfig?.path;
			if (path && path !== '') segments.unshift(path);
			current = current.parent;
		}
		return segments.join('/');
	}

	private getFirstAvailableChild(children: any[], parentPath: string): string | null {
		for (const child of children) {
			if (!child.path || child.path === '**' || child.path === '') continue;
			const permissions = child.data?.['permissions'] ?? [];
			const isAllowed = permissions.length === 0 || this.user.allowed(permissions);
			if (isAllowed) {
				return `${parentPath}/${child.path}`;
			}
		}
		return null;
	}

	private getFirstAvailableTopLevelRoute(): string | null {
		const order = Object.keys(routerLinks) as (keyof typeof routerLinks)[];
		for (const key of order) {
			const route = routerLinks[key];
			if (!route) continue;
			const parentPermissions = route.data?.permissions || [];
			const parentAccessible = parentPermissions.length === 0 || parentPermissions.every(p => this.user.allowed([p]));
			if (!parentAccessible) continue;

			if (route.children && route.children.length) {
				for (const child of route.children) {
					const childPermissions = child.data?.permissions || [];
					const childAccessible = childPermissions.length === 0 || childPermissions.every(p => this.user.allowed([p]));
					if (childAccessible && child.path && child.path !== '') {
						return `/${route.path}/${child.path}`;
					}
				}
				continue;
			} else {
				return `/${route.path}`;
			}
		}
		return null;
	}

	private findFirstAvailableUrl(route: ActivatedRouteSnapshot): string | null {
		const parentChildren = route.parent?.routeConfig?.children ?? [];
		if (parentChildren.length) {
			const parentPath = this.getFullPath(route.parent!);
			const siblingUrl = this.getFirstAvailableChild(parentChildren, parentPath);
			if (siblingUrl) return siblingUrl;
		}

		const ownChildren = route.routeConfig?.children ?? [];
		if (ownChildren.length) {
			const currentPath = this.getFullPath(route);
			const ownChildUrl = this.getFirstAvailableChild(ownChildren, currentPath);
			if (ownChildUrl) return ownChildUrl;
		}

		const topLevelUrl = this.getFirstAvailableTopLevelRoute();
		if (topLevelUrl) return topLevelUrl;

		return null;
	}
}

export const AuthGuard: CanActivateFn = (route) =>
	inject(AuthGuardService).canActivate(route);

export const AuthGuardChild: CanActivateChildFn = (route) =>
	inject(AuthGuardService).canActivateChild(route);